import { Close, CloseFullscreen, OpenInFull } from "@mui/icons-material";
import MaximizeIcon from "@mui/icons-material/Maximize";
import MinimizeIcon from "@mui/icons-material/Minimize";
import { Box, Grid, IconButton } from "@mui/material";
import clsx from "clsx";
import { useAtomValue, useSetAtom } from "jotai";
import React, { useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { animationsAtom, currentTheme } from "../../../atoms";
import { SmetaResizeElement } from "../../ComponentInterface";
import { CheckModalBackGrounds, getButtons } from "../../TokenProcessing/TokenProcessing";
import { getOptionsForUser, updateOptionsForUser } from "../../Url";
import {
  buttonLabels,
  ContextButtons,
  ModalWindowsButtonsObject,
  ModalWindowsProps,
  SmetaModalActionsContext,
  smetaModals,
  smetaModalsAtom,
  SmetaModalsButtonsContext,
  SmetaModalsIdContext,
} from "../Tools/modalManager";
import { Button as DlgButton, DlgRes } from "../Tools/ProjectX";
import { updateGridData } from "../Tools/Tools";
import { createModalLeaveScope, requestLeaveAtom } from "../Tools/leaveGuard";

const getContentButtonEl = (
  propsButtons: number | ModalWindowsButtonsObject | undefined,
  buttonsContext: ContextButtons,
) => {
  let contentButtonEl: {
    Buttons: number;
    jsonData: Record<string, string>;
    disabledButtons: { [key in DlgButton]?: boolean };
    hiddenButtons: { [key in DlgButton]?: boolean };
  };
  if (typeof propsButtons === "number") {
    contentButtonEl = { Buttons: propsButtons, jsonData: {}, disabledButtons: {}, hiddenButtons: {} };
  } else if (typeof propsButtons === "object") {
    contentButtonEl = {
      Buttons: Number(propsButtons.Params?.Buttons || propsButtons.Buttons || 0),
      jsonData: propsButtons.jsonData || {},
      disabledButtons: propsButtons.disabledButtons || {},
      hiddenButtons: propsButtons.hiddenButtons || {},
    };
  } else {
    contentButtonEl = {
      Buttons: DlgButton.Ok + DlgButton.Cancel,
      jsonData: { Кнопка$Выбрать: "Выбрать" },
      disabledButtons: {},
      hiddenButtons: {},
    };
  }
  Object.entries(buttonsContext).forEach(([key, value]) => {
    const keyButton = Number(key) as DlgButton;
    if (!(contentButtonEl.Buttons & keyButton)) contentButtonEl.Buttons += keyButton;
    if (value.disabled !== undefined) contentButtonEl.disabledButtons[keyButton] = value.disabled;
    if (value.label !== undefined) contentButtonEl.jsonData[buttonLabels[keyButton]] = value.label;
    if (value.hidden !== undefined) contentButtonEl.hiddenButtons[keyButton] = value.hidden;
  });
  return contentButtonEl;
};

const calculateNewPosition = (
  prevPos: number,
  initialWindowSize: number,
  initialSize: number,
  currentWindowSize: number,
) => {
  let newPos = (prevPos / (initialWindowSize - initialSize)) * (currentWindowSize - initialSize);
  if (isNaN(newPos) || newPos < 0) newPos = 0;
  return newPos;
};

// eslint-disable-next-line @typescript-eslint/naming-convention
function ModalWindows(props: ModalWindowsProps) {
  const theme = useAtomValue(currentTheme);
  const style = props.style || {};
  const modalRef = useRef<SmetaResizeElement>(null!);
  const parentElRef = useRef<HTMLDivElement>(null!);
  const resizeContainerRef = useRef<HTMLDivElement>(null!);
  const [hideModalFeedback, setHideModalFeedback] = useState(true);
  const modalFeedbackRef = useRef<HTMLDivElement>(null!);
  const posStartDrag = { x: 0, y: 0, left: 0, top: 0 };
  const posBeforeMax = useRef({ top: 0, left: 0 });
  const size = useRef({ height: "", width: "" });
  const [fullScreen, setFullScreen] = useState(false);
  const animationsOn = useAtomValue(animationsAtom);
  const [opacity, setOpacity] = useState(animationsOn ? "0" : "");
  const initialWindowSize = useRef({ width: window.innerWidth, height: window.innerHeight });
  const [minimized, setMinimized] = useState(false);
  const [title, setTitle] = useState(props.Title || "");
  const [buttonEl, setButtonEl] = useState<React.ReactNode>();
  const datasetId = useContext(SmetaModalsIdContext);
  const requestLeave = useSetAtom(requestLeaveAtom);
  const [buttonsContext, setButtonsContext] = useState<ContextButtons>({});
  const parentElZIndex = useRef("");
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const contentButtonEl = getContentButtonEl(props.Buttons, buttonsContext);
    setButtonEl(getButtons(contentButtonEl));
  }, [buttonsContext]);

  useEffect(() => {
    setTitle(props.Title || "");
  }, [props.Title]);

  useEffect(() => {
    if (props.closing) setOpacity("0");
  }, [props.closing]);

  useEffect(() => {
    modalRef.current.resize = windowResizeHandler;
    parentElZIndex.current = String(
      props.zIndex === undefined
        ? 1000 + CheckModalBackGrounds(parentElRef.current)
        : props.zIndex + CheckModalBackGrounds(parentElRef.current),
    );
    parentElZIndex.current = `z-index: ${parentElZIndex.current} !important`;
    parentElRef.current.style.cssText = parentElZIndex.current;
    if (!props.disableBlockBackground) blockBackgroundSwitch(true);
    const modalLocation = getOptionsForUser()?.modalsLocation?.[title];
    const currentLocation = modalLocation?.currentLocation;
    if (currentLocation && !props.blockSaveLocation) {
      const currentWidth = window.innerWidth;
      const currentHeight = window.innerHeight;
      modalRef.current.style.height = currentLocation.height;
      modalRef.current.style.width = currentLocation.width;
      size.current = modalLocation.size;
      if (currentLocation.height === "100%" && currentLocation.width === "100%") {
        parentElRef.current.setAttribute("blockbackground", "");
        modalRef.current.setAttribute("maximized", "");
        setFullScreen(true);
      }
      if (currentLocation.windowHeight !== currentHeight || currentLocation.windowWidth !== currentWidth) {
        if (currentLocation.windowHeight !== currentHeight) {
          modalRef.current.style.top =
            calculateNewPosition(
              parseFloat(currentLocation.top),
              currentLocation.windowHeight,
              modalRef.current.offsetHeight,
              currentHeight,
            ) + "px";
          posBeforeMax.current.top = calculateNewPosition(
            modalLocation.posBeforeMax.top,
            currentLocation.windowHeight,
            parseFloat(modalLocation.size.height),
            currentHeight,
          );
          if (size.current.height.endsWith("px")) {
            if (
              modalRef.current.hasAttribute("maximized") &&
              posBeforeMax.current.top === 0 &&
              parseFloat(size.current.height) > currentHeight
            ) {
              size.current.height = currentHeight + "px";
            } else if (modalRef.current.style.top === "0px" && parseFloat(size.current.height) > currentHeight) {
              size.current.height = currentHeight + "px";
              modalRef.current.style.height = size.current.height;
            }
          }
          modalRef.current.style.top = currentLocation.top;
          posBeforeMax.current.top = modalLocation.posBeforeMax.top;
        }
        if (currentLocation.windowWidth !== currentWidth) {
          modalRef.current.style.left =
            calculateNewPosition(
              parseFloat(currentLocation.left),
              currentLocation.windowWidth,
              modalRef.current.offsetWidth,
              currentWidth,
            ) + "px";
          posBeforeMax.current.left = calculateNewPosition(
            modalLocation.posBeforeMax.left,
            currentLocation.windowWidth,
            parseFloat(modalLocation.size.width),
            currentWidth,
          );
          if (size.current.width.endsWith("px")) {
            if (
              modalRef.current.hasAttribute("maximized") &&
              posBeforeMax.current.left === 0 &&
              parseFloat(size.current.width) > currentWidth
            ) {
              size.current.width = currentWidth + "px";
            } else if (modalRef.current.style.left === "0px" && parseFloat(size.current.width) > currentWidth) {
              size.current.width = currentWidth + "px";
              modalRef.current.style.width = size.current.width;
            }
          }
        } else {
          modalRef.current.style.left = currentLocation.left;
          posBeforeMax.current.left = modalLocation.posBeforeMax.left;
        }
      } else {
        modalRef.current.style.left = currentLocation.left;
        modalRef.current.style.top = currentLocation.top;
        posBeforeMax.current = modalLocation.posBeforeMax;
      }
    } else {
      let heightModal = props.style?.height || "30%",
        widthModal = props.style?.width || "40%";
      if (typeof heightModal === "number") heightModal = heightModal + "px";
      if (typeof widthModal === "number") widthModal = widthModal + "px";
      modalRef.current.style.height = heightModal;
      modalRef.current.style.width = widthModal;
      size.current = { height: heightModal, width: widthModal };
      saveModalParams();
      const bodyWidth = document.documentElement.clientWidth;
      const bodyHeight = document.documentElement.clientHeight;
      modalRef.current.style.left = (bodyWidth - modalRef.current.offsetWidth) / 2 + "px";
      modalRef.current.style.top = (bodyHeight - modalRef.current.offsetHeight) / 2 + "px";
    }
    if (animationsOn) setOpacity("");
    if (props.setFocus)
      setTimeout(() => {
        modalRef.current.focus();
      }, 1);
    bringToFront();
  }, []);

  useEffect(() => {
    blockBackgroundSwitch(props.hasBlockBackground!);
  }, [props.hasBlockBackground]);

  function bringToFront() {
    document.querySelectorAll<HTMLDivElement>(".background_ModalDialog[ontop]").forEach((el) => {
      el.removeAttribute("ontop");
    });
    parentElRef.current.setAttribute("ontop", "");
  }

  function blockBackgroundSwitch(block: boolean) {
    if (block) {
      parentElRef.current.setAttribute("blockbackground", "");
      if (props.disableBlockBackground && !props.hasBlockBackground) {
        parentElRef.current.style.zIndex = "9999";
      }
    } else {
      parentElRef.current.style.zIndex = parentElZIndex.current;
      if (!modalRef.current.hasAttribute("maximized")) parentElRef.current.removeAttribute("blockbackground");
    }
  }

  function windowResizeHandler() {
    const currentWidth = window.innerWidth;
    const currentHeight = window.innerHeight;
    if (currentWidth !== initialWindowSize.current.width) {
      if (modalRef.current.hasAttribute("maximized") || modalRef.current.hasAttribute("minimized")) {
        const newLeft = calculateNewPosition(
          posBeforeMax.current.left,
          initialWindowSize.current.width,
          parseFloat(size.current.width),
          currentWidth,
        );
        posBeforeMax.current.left = newLeft;
        if (newLeft === 0 && size.current.width.endsWith("px") && parseFloat(size.current.width) > currentWidth) {
          size.current.width = currentWidth + "px";
        }
      } else {
        const newLeft = calculateNewPosition(
          modalRef.current.offsetLeft,
          initialWindowSize.current.width,
          modalRef.current.offsetWidth,
          currentWidth,
        );
        modalRef.current.style.left = newLeft + "px";
        if (newLeft === 0 && modalRef.current.offsetWidth > currentWidth) {
          modalRef.current.style.width = currentWidth + "px";
        }
      }
    }
    if (currentHeight !== initialWindowSize.current.height) {
      if (modalRef.current.hasAttribute("maximized") || modalRef.current.hasAttribute("minimized")) {
        const newTop = calculateNewPosition(
          posBeforeMax.current.top,
          initialWindowSize.current.height,
          parseFloat(size.current.height),
          currentHeight,
        );
        posBeforeMax.current.top = newTop;
        if (newTop === 0 && size.current.height.endsWith("px") && parseFloat(size.current.height) > currentHeight) {
          size.current.height = currentHeight + "px";
        }
      } else {
        const newTop = calculateNewPosition(
          modalRef.current.offsetTop,
          initialWindowSize.current.height,
          modalRef.current.offsetHeight,
          currentHeight,
        );
        modalRef.current.style.top = newTop + "px";
        if (newTop === 0 && modalRef.current.offsetHeight > currentHeight) {
          modalRef.current.style.height = currentHeight + "px";
        }
      }
    }
    if (modalRef.current.hasAttribute("maximized") || modalRef.current.hasAttribute("minimized")) {
      saveModalParams();
    }
    initialWindowSize.current = { width: currentWidth, height: currentHeight };
  }

  function modalResizeHandler() {
    modalRef.current.querySelectorAll("[data-smetaresizer]").forEach((el) => (el as SmetaResizeElement).resize());
    setTimeout(() => {
      updateGridData(10, { last: true });
    });
  }

  function saveLocation() {
    if (!props.blockSaveLocation) {
      const modalsLocation = getOptionsForUser()?.modalsLocation || {};
      const currentLocation = {
        height: modalRef.current.style.height,
        width: modalRef.current.style.width,
        left: modalRef.current.style.left,
        top: modalRef.current.style.top,
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
      };
      const modalLocation = modalsLocation?.[title] || {};
      updateOptionsForUser({
        modalsLocation: {
          ...modalsLocation,
          [title]: {
            ...modalLocation,
            currentLocation: currentLocation,
          },
        },
      });
    }
  }

  function saveModalParams() {
    if (!props.blockSaveLocation) {
      const modalsLocation = getOptionsForUser()?.modalsLocation || {};
      const modalLocation = modalsLocation?.[title] || {};
      updateOptionsForUser({
        modalsLocation: {
          ...modalsLocation,
          [title]: {
            ...modalLocation,
            posBeforeMax: posBeforeMax.current,
            size: size.current,
          },
        },
      });
    }
  }

  function endMoving() {
    modalRef.current.removeAttribute("dragged");
    if (modalFeedbackRef.current.className === "modalWindowTopFeedback") {
      parentElRef.current.setAttribute("blockbackground", "");
      modalRef.current.style.height = "100%";
      modalRef.current.style.width = "100%";
      modalRef.current.style.top = "0";
      modalRef.current.style.left = "0";
      modalRef.current.setAttribute("maximized", "");
      setFullScreen(true);
      if (animationsOn) {
        setTimeout(() => {
          modalResizeHandler();
        }, 251);
      } else modalResizeHandler();
    }
    saveLocation();
    setHideModalFeedback(true);
    if (props.disableBlockBackground) blockBackgroundSwitch(false);
    document.removeEventListener("mousemove", moveModal);
    document.removeEventListener("touchmove", touchMoveHandler);
    document.removeEventListener("mouseup", endMoving);
    document.removeEventListener("touchend", endMoving);
  }

  function moveModal(event: MouseEvent | Touch) {
    if (!modalRef.current.hasAttribute("dragged")) {
      modalRef.current.setAttribute("dragged", "");
      posBeforeMax.current = { top: modalRef.current.offsetTop, left: modalRef.current.offsetLeft };
      saveModalParams();
    }
    if (modalRef.current.hasAttribute("maximized")) {
      modalRef.current.removeAttribute("maximized");
      setFullScreen(false);
      modalRef.current.style.height = size.current.height;
      modalRef.current.style.width = size.current.width;
      posStartDrag.left =
        event.clientX - parseFloat(window.getComputedStyle(modalRef.current).getPropertyValue("width")) / 2;
      modalResizeHandler();
    }
    let newLeft = Math.max(0, posStartDrag.left + event.clientX - posStartDrag.x);
    let newTop = Math.max(0, posStartDrag.top + event.clientY - posStartDrag.y);
    if (newLeft + modalRef.current.offsetWidth > document.documentElement.clientWidth) {
      newLeft = document.documentElement.clientWidth - modalRef.current.offsetWidth;
    }
    if (newTop + modalRef.current.offsetHeight > document.documentElement.clientHeight) {
      newTop = document.documentElement.clientHeight - modalRef.current.offsetHeight;
    }
    modalRef.current.style.left = newLeft + "px";
    modalRef.current.style.top = newTop + "px";
    if (event.pageY <= document.documentElement.offsetTop && !props.blockMaximize) {
      if (modalFeedbackRef.current.className !== "modalWindowTopFeedback") {
        modalFeedbackRef.current.className = "modalWindowTopFeedback";
      }
    } else if (modalFeedbackRef.current.className === "modalWindowTopFeedback") {
      modalFeedbackRef.current.removeAttribute("class");
    }
  }

  function touchMoveHandler(event: TouchEvent) {
    moveModal(event.changedTouches[0]);
    event.preventDefault();
  }

  function resizeStart() {
    document.addEventListener("mousemove", resizeHandler);
    document.addEventListener("mouseup", resizeEnd);
    parentElRef.current.style.userSelect = "none";
    setHideModalFeedback(false);
    if (props.disableBlockBackground) blockBackgroundSwitch(true);
  }

  function resizeEnd() {
    document.removeEventListener("mousemove", resizeHandler);
    document.removeEventListener("mouseup", resizeEnd);
    parentElRef.current.style.userSelect = "";
    if (props.disableBlockBackground) blockBackgroundSwitch(false);
    if (modalFeedbackRef.current.className === "modalWindowResizeFeedback") {
      modalRef.current.style.height = modalFeedbackRef.current.style.height;
      modalRef.current.style.width = modalFeedbackRef.current.style.width;
      modalRef.current.style.left = modalFeedbackRef.current.style.left;
      modalRef.current.style.top = modalFeedbackRef.current.style.top;
      modalRef.current.removeAttribute("resized");
      if (!modalRef.current.hasAttribute("maximized")) {
        size.current = { height: modalFeedbackRef.current.style.height, width: modalFeedbackRef.current.style.width };
        saveModalParams();
      }
      saveLocation();
      if (animationsOn) {
        setTimeout(() => {
          modalResizeHandler();
        }, 251);
      } else modalResizeHandler();
    }
    setHideModalFeedback(true);
  }

  function resize(side: string, event: MouseEvent) {
    const modalFeedbackRect = modalFeedbackRef.current.getBoundingClientRect(),
      modalRect = modalRef.current.getBoundingClientRect(),
      windowSize = { height: window.innerHeight, width: window.innerWidth };
    const minHeight = parseFloat(window.getComputedStyle(modalRef.current).getPropertyValue("min-height")),
      minWidth = parseFloat(window.getComputedStyle(modalRef.current).getPropertyValue("min-width"));
    let maxHeight = parseFloat(window.getComputedStyle(modalRef.current).getPropertyValue("max-height")),
      maxWidth = parseFloat(window.getComputedStyle(modalRef.current).getPropertyValue("max-width"));
    if (isNaN(maxHeight)) maxHeight = 0;
    if (isNaN(maxWidth)) maxWidth = 0;
    switch (side) {
      case "Top":
        // Ограничение сверху
        if (event.clientY >= 0) {
          const newHeight = modalFeedbackRef.current.offsetHeight - (event.clientY - modalFeedbackRect.top);
          // Ограничение снизу
          if (newHeight > minHeight) {
            if (maxHeight && newHeight > maxHeight) {
              modalFeedbackRef.current.style.top = modalRect.top + modalRect.height - maxHeight + "px";
              modalFeedbackRef.current.style.height = maxHeight + "px";
            } else {
              modalFeedbackRef.current.style.top = event.clientY + "px";
              modalFeedbackRef.current.style.height = newHeight + "px";
            }
          } else {
            modalFeedbackRef.current.style.top = modalRect.top + modalRect.height - minHeight + "px";
            modalFeedbackRef.current.style.height = minHeight + "px";
          }
        } else {
          const newHeight = modalRect.top + modalRect.height;
          if (maxHeight && newHeight > maxHeight) {
            modalFeedbackRef.current.style.top = newHeight - maxHeight + "px";
            modalFeedbackRef.current.style.height = maxHeight + "px";
          } else {
            modalFeedbackRef.current.style.top = "0px";
            modalFeedbackRef.current.style.height = newHeight + "px";
          }
        }
        break;
      case "Left":
        // Ограничение слева
        if (event.clientX >= 0) {
          const newWidth = modalFeedbackRef.current.offsetWidth - (event.clientX - modalFeedbackRect.left);
          // Ограничение справа
          if (newWidth > minWidth) {
            if (maxWidth && newWidth > maxWidth) {
              modalFeedbackRef.current.style.left = modalRect.left + modalRect.width - maxWidth + "px";
              modalFeedbackRef.current.style.width = maxWidth + "px";
            } else {
              modalFeedbackRef.current.style.left = event.clientX + "px";
              modalFeedbackRef.current.style.width = newWidth + "px";
            }
          } else {
            modalFeedbackRef.current.style.left = modalRect.left + modalRect.width - minWidth + "px";
            modalFeedbackRef.current.style.width = minWidth + "px";
          }
        } else {
          const newWidth = modalRect.left + modalRect.width;
          if (maxWidth && newWidth > maxWidth) {
            modalFeedbackRef.current.style.left = newWidth - maxWidth + "px";
            modalFeedbackRef.current.style.width = maxWidth + "px";
          } else {
            modalFeedbackRef.current.style.left = "0px";
            modalFeedbackRef.current.style.width = newWidth + "px";
          }
        }
        break;
      case "Bottom":
        maxHeight =
          maxHeight === 0
            ? windowSize.height - modalFeedbackRect.top
            : Math.min(maxHeight, windowSize.height - modalFeedbackRect.top);
        modalFeedbackRef.current.style.height =
          Math.min(maxHeight, Math.max(minHeight, event.clientY - modalFeedbackRect.top)) + "px";
        break;
      case "Right":
        maxWidth =
          maxWidth === 0
            ? windowSize.width - modalFeedbackRect.left
            : Math.min(maxWidth, windowSize.width - modalFeedbackRect.left);
        modalFeedbackRef.current.style.width =
          Math.min(maxWidth, Math.max(minWidth, event.clientX - modalFeedbackRect.left)) + "px";
        break;
      default:
        break;
    }
  }

  function resizeHandler(event: MouseEvent) {
    if (modalFeedbackRef.current.className !== "modalWindowResizeFeedback") {
      modalFeedbackRef.current.className = "modalWindowResizeFeedback";
      modalFeedbackRef.current.style.height = modalRef.current.offsetHeight + "px";
      modalFeedbackRef.current.style.width = modalRef.current.offsetWidth + "px";
      modalFeedbackRef.current.style.top = modalRef.current.style.top;
      modalFeedbackRef.current.style.left = modalRef.current.style.left;
      modalFeedbackRef.current.setAttribute(
        "side",
        resizeContainerRef.current.className.substring(
          resizeContainerRef.current.className.indexOf("resizing") + 8,
          resizeContainerRef.current.className.length,
        ),
      );
      modalRef.current.setAttribute("resized", "");
    }
    const side = modalFeedbackRef.current.getAttribute("side")!;
    switch (side) {
      case "TopLeft":
        resize("Top", event);
        resize("Left", event);
        break;
      case "BottomLeft":
        resize("Bottom", event);
        resize("Left", event);
        break;
      case "BottomRight":
        resize("Bottom", event);
        resize("Right", event);
        break;
      case "TopRight":
        resize("Top", event);
        resize("Right", event);
        break;
      default:
        resize(side, event);
        break;
    }
  }

  function mouseDownHandler(event: React.MouseEvent | React.TouchEvent) {
    const target = event.target as HTMLDivElement;
    if (props.disableBlockBackground && !modalRef.current.hasAttribute("ontop")) bringToFront();
    if (resizeContainerRef.current.className.indexOf("resizing") > -1) {
      resizeStart();
    } else if (target.classList.contains("headModal") || target.parentElement?.classList.contains("headModal")) {
      if ("touches" in event) {
        posStartDrag.x = event.targetTouches[0].clientX;
        posStartDrag.y = event.targetTouches[0].clientY;
      } else {
        posStartDrag.x = event.clientX;
        posStartDrag.y = event.clientY;
      }
      posStartDrag.left = modalRef.current.offsetLeft;
      posStartDrag.top = modalRef.current.offsetTop;
      setHideModalFeedback(false);
      if (props.disableBlockBackground) blockBackgroundSwitch(true);
      document.addEventListener("mousemove", moveModal);
      document.addEventListener("touchmove", touchMoveHandler, { passive: false });
      document.addEventListener("mouseup", endMoving);
      document.addEventListener("touchend", endMoving);
    }
  }

  function maximize() {
    if (!props.blockMaximize) {
      blockBackgroundSwitch(true);
      if (fullScreen) {
        modalRef.current.style.height = size.current.height;
        modalRef.current.style.width = size.current.width;
        modalRef.current.style.top = posBeforeMax.current.top + "px";
        modalRef.current.style.left = posBeforeMax.current.left + "px";
      } else {
        modalRef.current.style.height = "100%";
        modalRef.current.style.width = "100%";
        if (props.disableBlockBackground && !modalRef.current.hasAttribute("ontop")) bringToFront();
        if (!minimized) {
          posBeforeMax.current = { top: modalRef.current.offsetTop, left: modalRef.current.offsetLeft };
        } else {
          smetaModals.refreshMinimizedWindowsPosition(getAllMinimizedWindows());
          setMinimized(false);
          modalRef.current.removeAttribute("minimized");
        }
        saveModalParams();
        modalRef.current.style.top = "0";
        modalRef.current.style.left = "0";
      }
      modalRef.current.toggleAttribute("maximized");
      if (props.disableBlockBackground) blockBackgroundSwitch(false);
      setFullScreen(!fullScreen);
      saveLocation();
      if (animationsOn) {
        setTimeout(() => {
          modalResizeHandler();
        }, 251);
      } else modalResizeHandler();
    }
  }

  function getAllMinimizedWindows() {
    const allMinimizedWindows: HTMLDivElement[] = [];
    document.querySelectorAll<HTMLDivElement>(".modalWindow[minimized]").forEach((el) => {
      if (el !== modalRef.current) allMinimizedWindows.push(el);
    });
    allMinimizedWindows.sort((a, b) => a.getBoundingClientRect().right - b.getBoundingClientRect().right);
    return allMinimizedWindows;
  }

  function minimize() {
    if (minimized) {
      modalRef.current.style.height = size.current.height;
      modalRef.current.style.width = size.current.width;
      modalRef.current.style.top = posBeforeMax.current.top + "px";
      modalRef.current.style.left = posBeforeMax.current.left + "px";
      smetaModals.refreshMinimizedWindowsPosition(getAllMinimizedWindows());
    } else {
      const allMinimizedWindows = getAllMinimizedWindows();
      if (!fullScreen) posBeforeMax.current = { top: modalRef.current.offsetTop, left: modalRef.current.offsetLeft };
      if (allMinimizedWindows.length) {
        const lastMinimizedWindows = allMinimizedWindows[allMinimizedWindows.length - 1];
        const newLeft =
          lastMinimizedWindows.offsetLeft + lastMinimizedWindows.offsetWidth + 260 >
          document.documentElement.clientWidth
            ? lastMinimizedWindows.offsetLeft
            : lastMinimizedWindows.offsetLeft + lastMinimizedWindows.offsetWidth + 5;
        modalRef.current.style.left = newLeft + "px";
        modalRef.current.style.top = lastMinimizedWindows.offsetTop + "px";
      } else {
        modalRef.current.style.left = "5px";
        modalRef.current.style.top = window.innerHeight - 53 + "px";
      }
      modalRef.current.style.width = "250px";
      modalRef.current.style.height = "48px";
      setFullScreen(false);
      modalRef.current.removeAttribute("maximized");
      blockBackgroundSwitch(false);
    }
    modalRef.current.toggleAttribute("minimized");
    setMinimized(!minimized);
  }

  async function clickButtonHandler(event: React.MouseEvent) {
    const currentTarget = event.target instanceof Element ? event.target.closest("button") : null;
    if (!(currentTarget instanceof HTMLButtonElement)) return;
    let action = "";
    if (currentTarget.classList.contains("button_Modal_Close")) {
      action = "close";
    } else if (currentTarget.classList.contains("button_Modal_Select")) {
      action = "select";
    }

    const canLeave =
      props.callBacks === true
        ? await requestLeave({
            reason: "modal-close",
            scope: createModalLeaveScope(datasetId),
            value: currentTarget.value,
          })
        : true;
    if (!canLeave) return;
    const shouldClose = props.closingQuery ? await props.closingQuery(currentTarget.value) : true;
    const keyButton = Number(currentTarget.value) as DlgButton;
    await buttonsContext[keyButton]?.onClick?.();
    if (shouldClose) {
      switch (action) {
        case "close":
          await props.onClose?.(currentTarget.value);
          await smetaModals.close(datasetId);
          if (minimized) smetaModals.refreshMinimizedWindowsPosition(getAllMinimizedWindows());
          props.elementToFocusAfterModal?.focus();
          break;
        case "select":
          await props.onSelect?.(currentTarget.value);
          await smetaModals.close(datasetId);
          props.elementToFocusAfterModal?.focus();
          break;
      }
    }
  }

  async function handleKeyDown(ev: React.KeyboardEvent) {
    if (ev.key === "Escape") {
      closeButtonRef.current?.click();
    }
  }

  function moveHandler(event: React.MouseEvent) {
    const containerResize = resizeContainerRef.current;
    containerResize.classList.remove(
      "resizingBottomRight",
      "resizingTopRight",
      "resizingTopLeft",
      "resizingBottomLeft",
      "resizingTop",
      "resizingBottom",
      "resizingLeft",
      "resizingRight",
    );
    const modalRect = modalRef.current.getBoundingClientRect(),
      topPos = event.clientY < modalRect.top + 10,
      bottomPos = event.clientY > modalRect.bottom - 10,
      rightPos = event.clientX > modalRect.right - 10,
      leftPos = event.clientX < modalRect.left + 10;
    if (leftPos || rightPos)
      return topPos
        ? containerResize.classList.add("resizingTop" + (leftPos ? "Left" : "Right"))
        : bottomPos
          ? containerResize.classList.add("resizingBottom" + (leftPos ? "Left" : "Right"))
          : containerResize.classList.add("resizing" + (leftPos ? "Left" : "Right"));
    if (topPos || bottomPos) {
      if (rightPos) return containerResize.classList.add("resizing" + (topPos ? "TopRight" : "BottomRight"));
      if (leftPos) return containerResize.classList.add("resizing" + (topPos ? "TopLeft" : "BottomLeft"));
      containerResize.classList.add("resizing" + (topPos ? "Top" : "Bottom"));
    }
  }

  return (
    <div ref={parentElRef} className="background_ModalDialog" onKeyDown={handleKeyDown}>
      {!props.disableBlockBackground && (
        <div
          className="backgroundModalWindow"
          onClick={() => {
            modalRef.current.focus();
          }}
        />
      )}
      <div
        id={props.id}
        className={clsx("modalWindow", { light: theme === "light" })}
        style={{
          ...style,
          opacity: opacity,
        }}
        ref={modalRef}
        tabIndex={-1}
        onMouseDown={minimized ? undefined : mouseDownHandler}
        onTouchStart={minimized ? undefined : mouseDownHandler}
        onMouseMove={props.blockResize || fullScreen || minimized ? undefined : moveHandler}
        onMouseLeave={() => {
          resizeContainerRef.current.classList.remove(
            "resizingBottomRight",
            "resizingTopRight",
            "resizingTopLeft",
            "resizingBottomLeft",
            "resizingTop",
            "resizingBottom",
            "resizingLeft",
            "resizingRight",
          );
        }}
        data-smetaresizer=""
        data-modalid={datasetId}
        data-animation={animationsOn}
      >
        <div className="modalResizeContainer" ref={resizeContainerRef}>
          <div className={clsx("headModal", "headerStimate", { light: theme === "light" })} onDoubleClick={maximize}>
            <div className={clsx("fontColorStimate", { light: theme === "light" })}>{title}</div>
            <div className={clsx("iconButtonStimate", { light: theme === "light" })} style={{ whiteSpace: "nowrap" }}>
              {props.disableBlockBackground && !props.blockCollapse && (
                <IconButton size="small" onClick={minimize}>
                  {!minimized ? <MinimizeIcon fontSize="small" /> : <MaximizeIcon fontSize="small" />}
                </IconButton>
              )}
              {props.blockMaximize ? null : (
                <IconButton size="small" onClick={maximize} color="inherit">
                  {fullScreen ? <CloseFullscreen fontSize="small" /> : <OpenInFull fontSize="small" />}
                </IconButton>
              )}
              {!props.isCloseDisabled && (
                <IconButton
                  ref={closeButtonRef}
                  value={DlgRes.Cancel}
                  size="small"
                  onClick={clickButtonHandler}
                  className="button_Modal_Close"
                  color="inherit"
                >
                  <Close fontSize="small" />
                </IconButton>
              )}
            </div>
          </div>
          <div className="contentModal">
            <Box
              style={{
                width: "100%",
                height: props.hiddenButton ? "100%" : "calc(100% - 40px)",
                overflow: "auto",
              }}
            >
              <SmetaModalActionsContext.Provider value={{ setTitle }}>
                <SmetaModalsButtonsContext.Provider value={setButtonsContext}>
                  {props.children}
                </SmetaModalsButtonsContext.Provider>
              </SmetaModalActionsContext.Provider>
            </Box>
            {!props.hiddenButton && (
              <Box style={{ width: "100%", display: "flex", justifyContent: "flex-end" }}>
                <Grid
                  container
                  direction="row"
                  alignItems="center"
                  style={{ marginTop: "10px" }}
                  onClick={clickButtonHandler}
                >
                  {buttonEl}
                </Grid>
              </Box>
            )}
          </div>
        </div>
      </div>
      {!hideModalFeedback && <div ref={modalFeedbackRef} />}
    </div>
  );
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export default function ModalPortal() {
  const modals = useAtomValue(smetaModalsAtom);
  const hasBlockBackground = modals.some((modal) => modal.props.disableBlockBackground !== true);

  return createPortal(
    modals.map((modal) => {
      return (
        <SmetaModalsIdContext.Provider key={modal.datasetId} value={modal.datasetId}>
          <ModalWindows {...modal.props} hasBlockBackground={hasBlockBackground}>
            {modal.content}
          </ModalWindows>
        </SmetaModalsIdContext.Provider>
      );
    }),
    document.getElementById("ModalPortal")!,
  );
}
